// One library card in each of the design's states: reading, ready, ingredients-only, no recipe,
// failed (with Try again).
import { Link } from '../router';
import { Alert, ListIcon, VegMark } from '../icons';
import { duration, isInstagramURL, namesFor } from '../lib/format';
import { t as tr, useT } from '../i18n';
import { reelThumb, Thumb } from './Thumb';
import type { SavedReel, Script } from '../types';

/** Processing progress from the newest pipeline step: Got it → Listening → Writing. */
export function progressStep(r: SavedReel): 1 | 2 | 3 {
  switch (r.last_stage) {
    case 'transcribe': case 'enrich': case 'index': case 'done': return 3;
    case 'fetch': case 'audio': return 2;
    default: return 1;
  }
}

export function RecipeCard({ r, script, onRetry, retrying }: {
  r: SavedReel; script: Script; onRetry: (id: number) => void; retrying: boolean;
}) {
  const t = useT();
  const handle = r.creator_handle ? `@${r.creator_handle}` : '';
  if (r.status === 'queued' || r.status === 'processing') {
    const step = progressStep(r);
    return (
      <article class="card-processing" aria-live="polite">
        <div class="processing-label"><span class="dot" />{t('card.reading')}</div>
        <div class="display" style="font-weight:700;font-size:19px;line-height:1.3">{t('card.arrives')}</div>
        <div class="bars" style="grid-template-columns:repeat(3,minmax(0,1fr))" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step} aria-label={t('card.progress')}>
          {[1, 2, 3].map((i) => <div class={'bar' + (i < step ? ' on' : i === step ? ' half' : '')} />)}
        </div>
        <div style="display:flex;justify-content:space-between;font-size:13px;color:var(--muted)"><span>{t('card.stages')}</span><span>{handle}</span></div>
      </article>
    );
  }
  if (r.status === 'failed') {
    return (
      <article class="card" style="padding:16px;display:flex;flex-direction:column;gap:10px">
        <div style="display:flex;gap:10px;align-items:flex-start">
          <Alert style="flex:none;margin-top:2px;color:var(--danger)" />
          <div style="display:flex;flex-direction:column;gap:2px">
            <div class="display" style="font-weight:700;font-size:18px;line-height:1.3">{t('card.failed')}</div>
            <div class="muted" style="font-size:14px;line-height:1.5">{friendlyError(r.error)} {t('card.linkSafe')}</div>
          </div>
        </div>
        <button class="btn btn-outline" style="min-height:46px;border-radius:12px;font-size:15px" disabled={retrying} onClick={() => onRetry(r.id)}>
          {retrying ? t('common.tryingAgain') : t('common.tryAgain')}
        </button>
      </article>
    );
  }
  if (r.ingredient_count === 0 && r.step_count === 0) {
    return (
      <article class="card-dashed">
        <div class="display" style="font-weight:700;font-size:18px;line-height:1.3">{t('card.noRecipe')}</div>
        <div class="muted" style="font-size:14px;line-height:1.5">{t('card.noRecipeBody')}</div>
        <div style="display:flex;justify-content:space-between;align-items:center;font-size:13px;color:var(--muted)">
          <span>{handle}</span>
          {isInstagramURL(r.url) && <a href={r.url} target="_blank" rel="noopener" style="font-weight:700;font-size:14px;min-height:44px;display:flex;align-items:center">{t('common.watchReel')}</a>}
        </div>
      </article>
    );
  }
  const [primary, secondary] = namesFor({ ta: r.dish_name_ta, en: r.dish_name_en, tg: r.dish_name_translit }, script);
  const meta = [r.cuisine, duration(r.duration_sec)].filter(Boolean).join(' · ');
  const counts = [handle, t('n.ingredients', { n: r.ingredient_count }), r.step_count ? t('n.steps', { n: r.step_count }) : ''].filter(Boolean).join(' · ');
  const voice = r.spoken_language === 'ta' ? t('card.taVoice') : r.spoken_language === 'en' ? t('card.enVoice') : '';
  return (
    <Link href={`/r/${r.id}`} class="card recipe-card" style="flex-direction:row;gap:14px;align-items:flex-start">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="card" />
      <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:6px">
        <div class="card-meta">
          <VegMark veg={r.is_veg} />
          {meta && <span>{meta}</span>}
          {voice && <span class="badge">{voice}</span>}
        </div>
        <div class="name-primary" lang={script === 'ta' && r.dish_name_ta ? 'ta' : undefined}>{primary}</div>
        {secondary && <div class="name-secondary">{secondary}</div>}
        <div class="small muted">{counts}</div>
        {r.cooked_count > 0 && (
          <div class="pill" style="align-self:flex-start;margin-top:2px">
            {t('n.cooked', { n: r.cooked_count })}{r.verdict ? ` · “${t(`verdict.${r.verdict}`)}”` : ''}
          </div>
        )}
        {r.step_count === 0 && (
          <div class="note-strip"><ListIcon />{t('card.ingredientsOnly')}</div>
        )}
      </span>
    </Link>
  );
}

function friendlyError(err?: string): string {
  const e = (err || '').toLowerCase();
  if (e.includes('private') || e.includes('not available') || e.includes('removed')) return tr('card.private');
  if (e.includes('resolve host') || e.includes('timeout') || e.includes('connection')) return tr('card.dropped');
  return tr('card.wrong');
}

